import { inject, observer } from "mobx-react";
import React, { FC } from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { compose } from "recompose";

import PageTitle from "nvent-web/App/components/PageTitle";
import { UserStore } from "nvent-web/stores/User";

import AppStore from "nvent-web/stores/App";
import { Banner } from "./components/Banner";
import { CatalogBar } from "./components/CatalogBar";
import { RussianSmartBandOffer } from "./components/RussianSmartBandOffer";
import { getEmailByCountryCode } from "./getEmailByCountryCode";
import style from "./NonCertifiedProPage.module.scss";

interface NonCertifiedProPagePropsInner {
  user: UserStore;
  app: AppStore;
}

const NonCertifiedProPageInner: FC<NonCertifiedProPagePropsInner> = ({ user, app }) => {
  const features = [
    {
      iconClass: "awardIcon",
      descriptionMessageId: "becomeAccredited",
    },
    {
      iconClass: "warrantyIcon",
      descriptionMessageId: "obtainRights",
    },
    {
      iconClass: "computerIcon",
      descriptionMessageId: "obtainReferrals",
    },
    {
      iconClass: "discountIcon",
      descriptionMessageId: "quarterlyPromotions",
    },
    {
      iconClass: "handsIcon",
      descriptionMessageId: "attendEvents",
    },
  ];
  const stepsToGetStarted = [
    {
      stepNumber: 1,
      title: "toBegin",
      messageIdsList: ["signUpForTraining", "attendCertifiedProTraining"],
    },
    {
      stepNumber: 2,
      title: "whatWillYouLearn",
      messageIdsList: ["floorHeatingOverview", "installationDemonstration", "testingAndWarranty", "salesTechniques"],
    },
    {
      stepNumber: 3,
      title: "onceCompleted",
      messageIdsList: ["licenseNumber", "CertifiedProInstaller", "totalCareWarranty", "promotions"],
    },
  ];

  const bannerClasses = {
    image: style.bannerImage,
    text: style.bannerText,
  };

  return (
    <>
      <PageTitle />

      <section className={style.wrapper}>
        <h1>
          <FormattedMessage id="certifiedPage.nonCertified.certifiedPro" />
        </h1>

        <Banner classes={bannerClasses}>
          <FormattedMessage id="certifiedPage.nonCertified.banner" />
        </Banner>
      </section>

      {app.locale === "ru" && (
        <section className={style.wrapper}>
          <RussianSmartBandOffer />
        </section>
      )}

      <CatalogBar />

      <section className={style.wrapper}>
        <div className={style.iconsRow}>
          {features.map((feature) => {
            const { iconClass, descriptionMessageId } = feature;
            return (
              <div className={style.column} key={iconClass}>
                <div className={style[iconClass]} />
                <p className={style.columnDescription}>
                  <FormattedHTMLMessage id={`certifiedPage.nonCertified.${descriptionMessageId}`} />
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className={style.signUpBar}>
        <div className={style.signUpBarContent}>
          <h2 className={style.signUpTitle}>
            <FormattedMessage id="certifiedPage.nonCertified.signUp" />
          </h2>
          <p className={style.signUpSubtitle}>
            <FormattedMessage id="certifiedPage.nonCertified.signUpDescription" />
          </p>
          <a className={style.signUpButton} href={`mailto:${getEmailByCountryCode(user.companyAddress.countryCode)}`}>
            <FormattedMessage id="certifiedPage.nonCertified.signUpNow" />
          </a>
        </div>
      </section>

      <section className={style.wrapper}>
        <h1 className={style.stepsHeader}>
          <FormattedMessage id="certifiedPage.nonCertified.steps" />
        </h1>
        {stepsToGetStarted.map((step) => {
          const { stepNumber, title, messageIdsList } = step;
          return (
            <div className={style.stepsRow} key={stepNumber}>
              <div className={style.stepNumber}>
                <div className={style.step}>{stepNumber}</div>
              </div>
              <div>
                <h2 className={style.stepHeader}>
                  <FormattedMessage id={`certifiedPage.nonCertified.${title}`} />
                </h2>
                <ul className={style.stepList}>
                  {messageIdsList.map((messageId) => (
                    <li key={messageId}>
                      <FormattedHTMLMessage id={`certifiedPage.nonCertified.${messageId}`} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
};

export const NonCertifiedProPage = compose<NonCertifiedProPagePropsInner, {}>(
  inject("user", "app"),
  observer
)(NonCertifiedProPageInner);
